import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  IBillingAccount,
  IBillingDocument,
  IOdbBillCategoryDetail,
  IOdbBillCategoryDetailSection,
  IOdbBillCategoryDetailSectionItem,
} from '@dch/billing-business-logic';
import { ODB_THIRD_PARTY_L4_PAGE } from '../../config/my-bill.constants';
import { ISelectedThirdPartySectionInfo } from '../../models/pii.model';
import {
  ODB_THIRD_PARTY_L4_PAGE_SESSION_DATA_KEY,
  UtilsService,
} from '../../services/utils.service';
import { EnvironmentService } from '@dch/ng-common';
import { Store } from '@ngrx/store';
import { filter, tap } from 'rxjs/operators';
import { ApplicationState } from 'src/app/state/reducers';
import { selectCustomerAccount } from 'src/app/state/selectors/customer-account.selector';
import { OnDestroyHookDirective } from '../../common/hooks';

@Component({
  selector: 'my-bill-odb-third-party-level-three',
  templateUrl: './odb-third-party-level-three.component.html',
  standalone: false,
})
export class OdbThirdPartyLevelThreeComponent
  extends OnDestroyHookDirective
  implements OnInit
{
  @Input() billingAccount: IBillingAccount;
  @Input() billingDocument: IBillingDocument;
  @Input() openTransactionToPayIndex?: number;
  @Input() categoryDetails: IOdbBillCategoryDetail[];
  @Input() productSplitIdentifier: string;
  @Input() customerId?: number;
  @Input() customerGlobalId?: string;
  @Input() customerSubsegment?: string;
  @Input() customerMobile?: string;
  @Input() amount: string;
  isStaySignedIn = false;
  redirectLink = ODB_THIRD_PARTY_L4_PAGE;
  showModal = false;

  readonly customerAccount$ = this.store.select(selectCustomerAccount);

  constructor(
    private router: Router,
    private utilsService: UtilsService,
    private store: Store<ApplicationState>,
    private environmentService: EnvironmentService
  ) {
    super();
  }

  ngOnInit() {
    this.getSelectedCustomerAccountStaySignedInStatus();
  }

  private getSelectedCustomerAccountStaySignedInStatus() {
    this.addSubscription(
      this.customerAccount$
        .pipe(
          filter((customerAccount) => customerAccount !== undefined),
          tap((customerAccount) => {
            this.isStaySignedIn = customerAccount?.staySignedIn ?? false;
          })
        )
        .subscribe()
    );
  }

  getItemsQuantity(items: Array<IOdbBillCategoryDetailSectionItem>) {
    const quantity = items.reduce((itemsTotalQuantity, item) => {
      // Add null/undefined checks
      if (!item?.quantity || item.quantity.trim() === '') {
        return itemsTotalQuantity;
      }

      const numericQuantity = +item.quantity;
      return !isNaN(numericQuantity)
        ? itemsTotalQuantity + Math.abs(numericQuantity)
        : itemsTotalQuantity;
    }, 0);
    return quantity > 0 ? `(${quantity})` : '';
  }

  // Extract navigation logic into separate methods
  private shouldShowModal(): boolean {
    return this.environmentService.isSmoothAccess || this.isStaySignedIn;
  }

  private prepareNavigationData(
    selectedSection: IOdbBillCategoryDetailSection,
    categoryDetailsIndex: number,
    sectionIndex: number
  ): ISelectedThirdPartySectionInfo {
    return {
      billingAccount: this.billingAccount,
      billDocument: this.billingDocument,
      openTransactionToPayIndex: this.openTransactionToPayIndex,
      productSplitIdentifier: this.productSplitIdentifier,
      selectedSection,
      selectedSectionIdentifier: this.generateSectionIdentifier(
        this.productSplitIdentifier,
        categoryDetailsIndex,
        sectionIndex
      ),
      customerId: this.customerId,
      customerSubsegment: this.customerSubsegment,
      customerGlobalId: this.customerGlobalId,
      customerMobile: this.customerMobile,
    };
  }

  navigateToL4(
    selectedSection: IOdbBillCategoryDetailSection,
    categoryDetailsIndex: number,
    sectionIndex: number
  ) {
    if (this.shouldShowModal()) {
      this.showModal = true;
    } else {
      const navigationData = this.prepareNavigationData(
        selectedSection,
        categoryDetailsIndex,
        sectionIndex
      );
      this.utilsService.setValueInSessionStorage<ISelectedThirdPartySectionInfo>(
        ODB_THIRD_PARTY_L4_PAGE_SESSION_DATA_KEY,
        navigationData
      );
      this.router.navigate([ODB_THIRD_PARTY_L4_PAGE]);
    }
  }

  private generateSectionIdentifier(
    productSplitIdentifier: string,
    categoryIndex: number,
    sectionIndex: number
  ): string {
    const cleanProductId = productSplitIdentifier?.replace(/\s/g, '') || '';
    return `${cleanProductId}${categoryIndex}${sectionIndex}`;
  }

  // Add method to handle modal closure
  closeModal() {
    this.showModal = false;
  }

  // Consider adding modal timeout or user action handling
}
